import { useEffect } from 'react';
import { Controller } from 'react-hook-form';

import { Button, Container, InputText, useForm, yup, yupResolver } from '@dansk-metal/ui';

import { Form, FormFooter, FormHeader, FormWrapper } from '@web/components/form-wrapper/form-wrapper';
import { queryClient } from '@web/components/providers/react-query';
import { ThemedSkeleton } from '@web/components/themed-skeleton/themed-skeleton';

import { validationMessages } from '@web/constants/messages/validation-messages';

import { useAuthenticatedCRMRequestConfig, useGetUser } from '@web/services/crm-api/hooks';
import { ErrorResult, getGetMemberByIdQueryKey, usePutMember } from '@web/services/crm-api/rest';
import { crmError } from '@web/services/errorhandler/appError';

import styles from './membership-contact-info.module.scss';

const { email, phone_number } = validationMessages;

const schema = yup.object({
	email: yup.string().required(email.empty).matches(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g, email.error),
	phoneNumber: yup.string()
		.matches(/^[0-9+]+$/, phone_number.error)
		.test('length', phone_number.error, (value) => {
			if (!value) return true;
			return	value?.replaceAll(' ', '').length >= 6 && value?.replaceAll(' ', '').length <= 20;
		}),
});

type ContactInfoPayload = yup.InferType<typeof schema>;

export function MembershipContactInfo() {
	const { requestConfig, memberId, isMember } = useAuthenticatedCRMRequestConfig();
	const { data, error, isLoading } = useGetUser();
	const { mutateAsync, isLoading: isSubmitting, isError, isSuccess } = usePutMember({ request: requestConfig });

	const { control, handleSubmit, reset, setError, formState } = useForm<ContactInfoPayload>({
		defaultValues: {
			email: '',
			phoneNumber: '',
		},
		mode: 'onSubmit',
		reValidateMode: 'onSubmit',
		resolver: yupResolver(schema),
	});

	async function onSubmit(payload: ContactInfoPayload) {
		try {
			if (!data) {
				throw new Error('Member data not ready');
			}

			const result = await mutateAsync({
				memberId,
				data: {
					email: payload.email,
					phoneNumber: payload.phoneNumber ? payload.phoneNumber.replaceAll(' ', '') : null,
				},
			});

			reset({
				email: result.email || '',
				phoneNumber: result.phoneNumber ? result.phoneNumber.replaceAll(' ', '') : '',
			});

			queryClient.invalidateQueries({ queryKey: getGetMemberByIdQueryKey(memberId) });
		} catch (err) {
			setError('root', {
				message: (err?.response?.data as ErrorResult | undefined)?.publicMessage || 'Der skete en fejl. Prøv igen senere.',
			});
		}
	}

	useEffect(() => {
		if (data) {
			reset({
				email: data.email || '',
				phoneNumber: data.phoneNumber ? data.phoneNumber.replaceAll(' ', '') : '',
			});
		}
	}, [data, reset]);

	if (error) {
		throw crmError({ cause: error });
	}

	return (
		<Container className={styles.container}>
			{isLoading && !data
				? (
					<ThemedSkeleton className={styles.skeleton_loader} />
				) : (
					<form onSubmit={handleSubmit(onSubmit)}>
						<FormWrapper>
							<div className={styles.banner_wrapper}>
								{(formState.errors.email || formState.errors.phoneNumber || isError) && <FormHeader type='error' message={isError ? 'Noget gik galt - prøv igen senere' : 'Der er fejl i et eller flere felter'} />}
								{(isSuccess && !(formState.errors.email || formState.errors.phoneNumber)) && <FormHeader type='success' message='Dine oplysninger er blevet gemt' />}
							</div>
							<div className={styles.form_details}>
								<p className={styles.header}>Navn og adresse</p>
								<p>{`${data?.firstName || ''} ${data?.lastName || ''}` || ''}
									<br />
									{data?.address?.addressLine}
									<br />
									{`${data?.address?.zipCode || ''} ${data?.address?.city || ''}`}
								</p>
								<p className={styles.info_text}>Dit navn og adresse er hentet fra CPR registret. Opdatér på borger.dk</p>
							</div>
							<Form>
								<Controller
									control={control}
									name="email"
									render={({ field, fieldState }) => (
										<InputText fieldState={fieldState} className={styles.input_text} {...field} noOptionalLabel label="Email" disabled={!isMember || isSubmitting} />
									)}
								/>
								<Controller
									control={control}
									name="phoneNumber"
									render={({ field, fieldState }) => (
										<InputText fieldState={fieldState} className={styles.input_text} {...field} noOptionalLabel label="Telefon (mobil)" disabled={!isMember || isSubmitting} />
									)}
								/>
							</Form>
						</FormWrapper>
						<FormFooter>
							<Button disabled={!isMember || isSubmitting} className={styles.button} type='submit' icon={isSubmitting ? { icon: 'spinner' } : { icon: 'checkmark' }}>Gem ændringer</Button>
						</FormFooter>
					</form>
				)}
		</Container>
	);
}
